import React from 'react';
import styled from 'styled-components';
import { colors } from '../../../styles/colors.ts';
import Modal from '../../Modal';
import ReactDOM from 'react-dom';
import Carrousel from '../../Carrousel';

const NarrowContent = styled.div`
  max-width: 800px;
  display: flex;
  flex-direction: column;
  gap: 4rem;
  width: 100%;
`;

const TitleWrapper = styled(NarrowContent)`
    gap: 1rem;
`;

const WideContent = styled.div`
  max-width: 1200px;
  display: flex;
  flex-direction: column;
  gap: 4rem;
  width: 100%;
`;

const Title = styled.h1`
  font-size: 2rem;
`;

const Subtitle = styled.h2`
  font-size: 1.2rem;
  font-weight: 400;
  margin: 0;
`;

const Paragraph = styled.p`
  margin: 0;
  line-height: 1.8;
  
  @media (max-width: 768px) {
    font-size: 14px;
  }
`;


const TagContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: 0.5rem;
  padding: 1rem 0;
`;

const ToolTag = styled.span`
  padding: 0.25rem 1.2rem;
  background-color: ${colors.transparent};
  border-radius: 3rem;
  border: 1px solid ${colors.neutral[200]};
  font-size: 0.8rem;
  color: ${colors.neutral[400]};
`;

const SplitSection = styled.div`
  display: flex;
  gap: 4rem;
  align-items: flex-start;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 2rem;
  }
`;

const SplitContentContainer = styled.div`
  flex: 1;
  position: relative;
  width: 100%;
  
  &.desktop-only {
    @media (max-width: 768px) {
      display: none;
    }
  }
  
  &.mobile-only {
    display: none;
    @media (max-width: 768px) {
      display: block;
    }
  }
`;



const SplitImage = styled.img`
  width: 100%;
  height: auto;
  object-fit: contain;
  border-radius: 8px;
  position: relative;

  @media (max-width: 768px) {
    position: relative;
  }
`;

const ImagePlaceholder = styled.img`
  width: 100%;
  height: auto;
  object-fit: contain;
  border-radius: 8px;
  position: relative;
`;

const ContentSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
  max-width: ${props => props.wide ? '800px' : '100%'};

  & ol {
    list-style-position: inside;
    display: flex;
    flex-direction: column;
    gap: 0.8rem;
  }

`;

const ImageSection = styled.div`
  width: 100%;
`;

const ImageLabel = styled.div`
    padding: 0.5rem 2rem;
    background: ${colors.neutral[100]}99;
    border: solid 1px ${colors.neutral[100]};
    -webkit-backdrop-filter: blur(1rem);
    backdrop-filter: blur(1rem);
    color: ${colors.black};
    font-size: 0.8rem;
    text-align: center;
    border-radius: 1rem;
    width: fit-content;
    margin: 1rem auto 0;
`;

const ConclusionWrapper = styled(NarrowContent)`
  padding: 6rem;
  text-align: center;
  align-items: center;
  position: relative;
  overflow: hidden;
  
  @media (max-width: 768px) {
    padding: 2rem 0;
    text-align: center;
    align-items: center;
  }
`;

const BackgroundImage = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -1;
  background-image: ${props => `url(${props.src})`};
  background-size: cover;
  background-position: ${props => props.align === 'bottom' ? 'bottom' : 'top'};
  background-repeat: no-repeat;
`;

const ConclusionTitle = styled.h2`
  font-size: 1.8rem;
  margin: 0;
`;

const ConclusionSubtitle = styled.h3`
  font-size: 1.4rem;
  font-weight: 400;
  margin: 0;
  line-height: 1.4;
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const ConclusionImage = styled.img`
  width: 100%;
  max-width: 600px;
  height: auto;
  object-fit: contain;
  border-radius: 8px;
  margin-top: 2rem;
`;

const SubtitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const HeroImageWrapper = styled.div`
  width: 100%;
  position: relative;
`;

const HeroImageDesktop = styled.img`
  width: 100%;
  height: auto;
  max-height: 640px;
  border-radius: 8px;
  object-fit: cover;

  @media (max-width: 768px) {
    display: none;
  }
`;

const HeroImageMobile = styled.img`
  width: 100%;
  height: auto;
  border-radius: 8px;
  object-fit: cover;
  display: none;

  @media (max-width: 768px) {
    display: block;
  }
`;

export const HeroImage = ({ src, mobileSrc, alt, onClick }) => {
  return (
    <HeroImageWrapper>
      <HeroImageDesktop src={src} alt={alt} onClick={onClick} />
      <HeroImageMobile src={mobileSrc || src} alt={alt} onClick={onClick} />
    </HeroImageWrapper>
  );
};

export const HighlightedParagraph = styled.div`
  max-width: 800px;
  display: flex;
  flex-direction: column;
  margin-right: auto;
  padding: 2rem;
  gap: 1rem;

  & span {
    font-size: 0.8rem;
    font-family: 'Stretch Pro';
    color: ${colors.neutral[400]};
  }

  & h2 {
    font-size: 1.5rem;
  }
  & h3 {
    font-size: 1.2rem;
  }

  @media (max-width: 768px) {
    padding: 0 0 0 4rem;

    & h2 {
      font-size: 1.2rem;
    }

    & h3 {
      font-size: 1rem;
    }
  }
`;

export const TitleSection = ({ title, tags, description }) => {
  return (
    <TitleWrapper>
      <Title>{title}</Title>
      {tags && (
        <TagContainer>
          {tags.map((tag, index) => (
            <ToolTag key={index}>{tag}</ToolTag>
          ))}
        </TagContainer>
      )}
      {Array.isArray(description) ? (
        description.map((text, index) => (
          <Paragraph key={index}>{text}</Paragraph>
        ))
      ) : (
        <Paragraph>{description}</Paragraph>
      )}
    </TitleWrapper>
  );
};

export const ProjectContent = ({ 
  title, 
  description,
  image, // Can be string, array, or React component
  mobileImages, // Optional array of mobile-specific images
  split = false, 
  direction = 'left',
  wide = false,
  zoom = false,
  carouselProps = {},
  label,
  children // Add children prop
}) => {
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [selectedImageData, setSelectedImageData] = React.useState(null);
  const ContentWrapper = wide ? WideContent : NarrowContent;

  const renderContent = () => {
    // If children are provided, render them directly
    if (children) {
      return children;
    }

    // Otherwise, fall back to the paragraph rendering logic
    if (!description) return null;

    // Handle both string and array inputs
    const paragraphs = Array.isArray(description) 
      ? description 
      : [description];
    
    return paragraphs.map((text, index) => (
      <Paragraph key={index}>{text}</Paragraph>
    ));
  };

  const handleImageClick = (imageData) => {
    if (zoom) {
      setSelectedImageData(imageData);
      setIsModalOpen(true);
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedImageData(null);
  };

  // Create a portal for the modal
  const renderModal = () => {
    if (!isModalOpen || !selectedImageData) return null;
    
    return ReactDOM.createPortal(
      <Modal 
        image={selectedImageData}
        onClose={handleModalClose}
      />,
      document.body
    );
  };

  const ImageWithModal = ({ src, alt, index = 0 }) => (
    <>
      <div style={{ position: 'relative' }}>
        <SplitImage 
          src={src} 
          alt={alt} 
          onClick={() => handleImageClick({ src, alt, index })}
        />
      </div>
      {label && (
        <ImageLabel>
          {Array.isArray(label) ? label[index] : label}
        </ImageLabel>
      )}
      {renderModal()}
    </>
  );

  const renderImageContent = () => {
    if (!image) return null;

    // If image is a React component, render it directly
    if (React.isValidElement(image)) {
      return (
        <ImageSection>
          {image}
        </ImageSection>
      );
    }

    if (Array.isArray(image)) {
      return (
        <ImageSection>
          <Carrousel 
            images={image} 
            mobileImages={mobileImages}
            onImageClick={handleImageClick}
            condensed
            label={label}
            zoom={zoom}
            {...carouselProps}
          />
          {renderModal()}
        </ImageSection>
      );
    }

    return (
      <ImageSection>
        <div style={{ position: 'relative' }}>
          <ImagePlaceholder 
            src={image} 
            alt={title || "Project content"} 
            onClick={() => handleImageClick({ src: image, alt: title || "Project content" })}
          />
        </div>
        {label && (
          <ImageLabel>
            {Array.isArray(label) ? label[0] : label}
          </ImageLabel>
        )}
        {renderModal()}
      </ImageSection>
    );
  };

  if (!split) {
    return (
      <ContentWrapper>
        {(title || description) && (
          <ContentSection>
            {title && <Subtitle>{title}</Subtitle>}
            {renderContent()}
          </ContentSection>
        )}
        {renderImageContent()}
      </ContentWrapper>
    );
  }

  const ContentComponent = (
    <ImageSection>
      <SplitContentContainer className={direction === 'left' ? 'desktop-only' : ''}>
        {React.isValidElement(image) ? image : (
          Array.isArray(image) ? (
            <Carrousel 
              images={image} 
              mobileImages={mobileImages}
              onImageClick={handleImageClick}
              condensed
              label={label}
              zoom={zoom}
              {...carouselProps}
            />
          ) : (
            <ImageWithModal src={image} alt={title || "Project content"} />
          )
        )}
      </SplitContentContainer>
    </ImageSection>
  );

  const MobileContentComponent = direction === 'left' ? (
    <ImageSection>
      <SplitContentContainer className="mobile-only">
        {React.isValidElement(image) ? image : (
          Array.isArray(image) ? (
            <Carrousel 
              images={image} 
              mobileImages={mobileImages}
              onImageClick={handleImageClick}
              condensed
              label={label}
              zoom={zoom}
              {...carouselProps}
            />
          ) : (
            <ImageWithModal src={image} alt={title || "Project content"} />
          )
        )}
      </SplitContentContainer>
    </ImageSection>
  ) : null;

  const ContentWrapperComponent = (
    <ContentWrapper wide={wide}>
        {(title || description) && (
          <ContentSection>
            {title && <Subtitle>{title}</Subtitle>}
            {renderContent()}
          </ContentSection>
        )}
        {MobileContentComponent}
    </ContentWrapper>
  );

  return (
    <ContentWrapper>
      <SplitSection>
        {direction === 'left' ? (
          <>
            {ContentComponent}
            {ContentWrapperComponent}
          </>
        ) : (
          <>
            {ContentWrapperComponent}
            {ContentComponent}
          </>
        )}
      </SplitSection>
    </ContentWrapper>
  );
};

export const ConclusionSection = ({ title, subtitle, image, backgroundImage, backgroundAlign = 'top' }) => {
  if (!title && !subtitle && !image && !backgroundImage) return null;

  return (
    <ConclusionWrapper>
      {backgroundImage && (
        <BackgroundImage 
          src={backgroundImage} 
          align={backgroundAlign}
        />
      )}
      {title && <ConclusionTitle>{title}</ConclusionTitle>}
      {subtitle && (
        Array.isArray(subtitle) ? (
          <SubtitleContainer>
            {subtitle.map((text, index) => (
              <ConclusionSubtitle key={index}>{text}</ConclusionSubtitle>
            ))}
          </SubtitleContainer>
        ) : (
          <ConclusionSubtitle>{subtitle}</ConclusionSubtitle>
        )
      )}
      {image && <ConclusionImage src={image} alt={title || "Conclusion image"} />}
    </ConclusionWrapper>
  );
};
